import React from 'react';
import { 
    Search, 
    Mail, 
    MessageSquare, 
    MousePointer2, 
    Link as LinkIcon, 
    Share2, 
    Globe, 
    HelpCircle,
    Users,
    ShoppingCart,
    Star,
    Video,
    Monitor,
    RefreshCw,
    Zap
} from 'lucide-react';

interface SourceIconProps {
    name: string;
    className?: string;
    style?: React.CSSProperties;
    color?: string;
}

const CDN_URL = (process.env.CDN_URL || 'https://cdn.dashbal.com/').replace(/\/$/, '');

const SOURCE_ICON_MAP: Record<string, string> = {
    'instagram': `${CDN_URL}/logos/channel_logos/instagram/instagram_primary.svg`,
    'facebook': `${CDN_URL}/logos/channel_logos/facebook/facebook_primary.png`,
    'tiktok': `${CDN_URL}/logos/channel_logos/tiktok/tiktok_primary.png`,
    'meta': `${CDN_URL}/logos/channel_logos/meta/meta_primary.png`,
    'shopify': `${CDN_URL}/logos/channel_logos/shopify/shopify_primary.png`,
    'woocommerce': `${CDN_URL}/logos/channel_logos/woocommerce/Woo_primary.png`,
    'google': `${CDN_URL}/logos/channel_logos/google/google_primary.svg`,
    'pinterest': `${CDN_URL}/logos/channel_logos/pinterest/pinterest_primary.svg`,
    'snapchat': `${CDN_URL}/logos/channel_logos/snapchat/snapchat_primary.svg`,
    'linkedin': `${CDN_URL}/logos/channel_logos/linkedin/linkedin_primary.png`,
    'x': `${CDN_URL}/logos/channel_logos/x/x_primary.png`,
};

export const SourceIcon: React.FC<SourceIconProps> = React.memo(({ name, className = "w-4 h-4", style, color }) => {
    const [imgError, setImgError] = React.useState(false);
    const lowerName = (name || '').toLowerCase();
    
    const { iconUrl, iconStyle } = React.useMemo(() => {
        const url = Object.entries(SOURCE_ICON_MAP).find(([key]) => lowerName.includes(key))?.[1];
        const ist = { color, ...style };
        return { iconUrl: url, iconStyle: ist };
    }, [lowerName, color, style]);
    
    // Ensure the icon is always centered within the provided dimensions
    const containerClasses = `${className} flex items-center justify-center shrink-0`;

    if (iconUrl && !imgError) {
        return (
            <div className={containerClasses} style={style}>
                <img 
                    src={iconUrl} 
                    alt={name} 
                    className="max-w-full max-h-full w-auto h-auto" 
                    style={{ objectFit: 'contain' }}
                    onError={() => setImgError(true)}
                />
            </div>
        );
    }

    const LucideIcon = (() => {
        if (lowerName.includes('google') || lowerName.includes('search') || lowerName.includes('seo') || lowerName.includes('adwords')) {
            return Search;
        }
        if (lowerName.includes('email') || lowerName.includes('newsletter') || lowerName.includes('mail') || lowerName.includes('klaviyo')) {
            return Mail;
        }
        if (lowerName.includes('sms') || lowerName.includes('message') || lowerName.includes('attentive') || lowerName.includes('postscript')) {
            return MessageSquare;
        }
        if (lowerName.includes('direct')) {
            return MousePointer2;
        }
        if (lowerName.includes('referral') || lowerName.includes('link')) {
            return LinkIcon;
        }
        if (lowerName.includes('affiliate')) {
            return Users;
        }
        if (lowerName.includes('influencer')) {
            return Star;
        }
        if (lowerName.includes('amazon') || lowerName.includes('shop') || lowerName.includes('store')) {
            return ShoppingCart;
        }
        if (lowerName.includes('video') || lowerName.includes('youtube')) {
            return Video;
        }
        if (lowerName.includes('display') || lowerName.includes('ads') || lowerName.includes('banner')) {
            return Monitor;
        }
        if (lowerName.includes('retention') || lowerName.includes('crm')) {
            return RefreshCw;
        }
        if (lowerName.includes('paid')) {
            return Zap;
        }
        if (lowerName.includes('social') || lowerName.includes('share')) {
            return Share2;
        }
        if (lowerName.includes('organic') || lowerName.includes('globe')) {
            return Globe;
        }
        return HelpCircle;
    })();

    return (
        <div className={containerClasses} style={style}>
            <LucideIcon 
                className="w-full h-full" 
                strokeWidth={2.5} 
                style={{ ...iconStyle, color: color || 'currentColor' }} 
            />
        </div>
    );
});
